import React, { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";

const AllUsersFilters = ({ initialFilters, initialStartDate, initialEndDate, onApply, onClear, loading }) => {
    const [filters, setFilters] = useState(initialFilters);
    const [startDate, setStartDate] = useState(initialStartDate || "");
    const [endDate, setEndDate] = useState(initialEndDate || "");
    const [searchTerm, setSearchTerm] = useState("");
    const [showAdvanced, setShowAdvanced] = useState(true);

    const [isGenderOpen, setIsGenderOpen] = useState(false);
    const genderRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (genderRef.current && !genderRef.current.contains(event.target)) {
                setIsGenderOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [genderRef]);

    const isFirstRun = useRef(true);
    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }
        const handler = setTimeout(() => {
            onApply({ filters: { ...filters, search: searchTerm }, startDate, endDate });
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const handleFilterChange = (e, field) => {
        const value = e.target.value;
        const newFilters = { ...filters, [field]: value };
        setFilters(newFilters);
        onApply({ filters: { ...newFilters, search: searchTerm }, startDate, endDate });
    };

    const handleDateChange = (type, value) => {
        const newStart = type === 'start' ? value : startDate;
        const newEnd = type === 'end' ? value : endDate;
        if (type === 'start') setStartDate(value);
        else setEndDate(value);
        onApply({ filters: { ...filters, search: searchTerm }, startDate: newStart, endDate: newEnd });
    };

    const handleClear = () => {
        setFilters({
            userId: "", age: "", gender: "", location: "",
            loginStatus: "", createdDate: "", userDataFlag: "", deviceType: ""
        });
        setStartDate("");
        setEndDate("");
        setSearchTerm("");
        onClear();
    };

    return (
        <div className="all-users-filters-wrapper">
            <div className="date-range-section">
                <span className="date-range-label">{filters.dateFilterType === 'lastActive' ? 'Active Date Range:' : 'Signup Date Range:'}</span>
                <div className="date-range-inputs">
                    <input type="date" className="started-dateRange" value={startDate} onChange={(e) => handleDateChange('start', e.target.value)} />
                    <span className="date-separator">to</span>
                    <input type="date" className="ended-dateRange" value={endDate} onChange={(e) => handleDateChange('end', e.target.value)} />
                </div>
                {(startDate || endDate || searchTerm || Object.entries(filters).some(([k, v]) => k === 'dateFilterType' ? v !== 'createdDate' : v !== "")) && (
                    <button className="clear-filters-btn" onClick={handleClear}>
                        Clear Filters
                    </button>
                )}
            </div>

            <div className="advanced-filters-container">
                <div className="advanced-filters-grid">
                    <div className="filter-group">
                        <label className="filter-label">USER ID</label>
                        <div className="filter-input-wrapper">
                            <input type="text" className="modern-filter-input" style={{ paddingLeft: '12px' }} value={filters.userId} onChange={(e) => handleFilterChange(e, 'userId')} placeholder="Ex: 6960..." />
                        </div>
                    </div>
                    <div className="filter-group">
                        <label className="filter-label">AGE</label>
                        <select className="modern-filter-select" value={filters.age} onChange={(e) => handleFilterChange(e, 'age')}>
                            <option value="">All</option>
                            <option value="0-18">0-18</option>
                            <option value="19-25">19-25</option>
                            <option value="26-35">26-35</option>
                            <option value="36-45">36-45</option>
                            <option value="46-60">46-60</option>
                            <option value="Above 60">Above 60</option>
                        </select>
                    </div>
                    <div className="filter-group" ref={genderRef}>
                        <label className="filter-label">GENDER</label>
                        <div style={{ position: 'relative', width: '100%' }}>
                            <button
                                type="button"
                                onClick={() => setIsGenderOpen(!isGenderOpen)}
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px 14px',
                                    backgroundColor: '#fff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: '#334155',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        backgroundColor: filters.gender === 'Male' ? '#3b82f6' : filters.gender === 'Female' ? '#ec4899' : filters.gender === 'Others' ? '#f59e0b' : '#94a3b8'
                                    }}></span>
                                    <span>{filters.gender === 'Male' ? 'Male' : filters.gender === 'Female' ? 'Female' : filters.gender === 'Others' ? 'Others' : 'All'}</span>
                                </div>
                                <ChevronDown size={16} style={{ color: '#94a3b8', transform: isGenderOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                            </button>

                            {isGenderOpen && (
                                <div style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 4px)',
                                    left: 0,
                                    width: '100%',
                                    backgroundColor: '#fff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                    zIndex: 50,
                                    padding: '4px',
                                    boxSizing: 'border-box'
                                }}>
                                    {[
                                        { value: '', label: 'All', color: '#94a3b8' },
                                        { value: 'Male', label: 'Male', color: '#3b82f6' },
                                        { value: 'Female', label: 'Female', color: '#ec4899' },
                                        { value: 'Others', label: 'Others', color: '#f59e0b' }
                                    ].map((option) => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            onClick={() => {
                                                handleFilterChange({ target: { value: option.value } }, 'gender');
                                                setIsGenderOpen(false);
                                            }}
                                            style={{
                                                width: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '8px 10px',
                                                border: 'none',
                                                backgroundColor: filters.gender === option.value ? '#f1f5f9' : 'transparent',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                color: '#334155',
                                                textAlign: 'left',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseOver={(e) => {
                                                if (filters.gender !== option.value) e.currentTarget.style.backgroundColor = '#f8fafc';
                                            }}
                                            onMouseOut={(e) => {
                                                if (filters.gender !== option.value) e.currentTarget.style.backgroundColor = 'transparent';
                                            }}
                                        >
                                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: option.color }}></span>
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="filter-group">
                        <label className="filter-label">DEVICE</label>
                        <select className="modern-filter-select" value={filters.deviceType} onChange={(e) => handleFilterChange(e, 'deviceType')}>
                            <option value="">All</option>
                            <option value="A">Android</option>
                            <option value="I">iOS</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <label className="filter-label">LOCATION</label>
                        <input type="text" className="modern-filter-input" style={{ paddingLeft: '12px' }} value={filters.location} onChange={(e) => handleFilterChange(e, 'location')} placeholder="Filter by city..." />
                    </div>
                    <div className="filter-group">
                        <label className="filter-label">CREATED DATE</label>
                        <input type="date" className="started-dateRange" style={{ paddingLeft: '12px' }} value={filters.createdDate} onChange={(e) => handleFilterChange(e, 'createdDate')} />
                    </div>
                    <div className="filter-group">
                        <label className="filter-label">PROFILE STATUS</label>
                        <select className="modern-filter-select" value={filters.userDataFlag} onChange={(e) => handleFilterChange(e, 'userDataFlag')}>
                            <option value="">All</option>
                            <option value="A">Completed</option>
                            <option value="I">Incomplete</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <label className="filter-label">STATUS</label>
                        <select className="modern-filter-select" value={filters.loginStatus} onChange={(e) => handleFilterChange(e, 'loginStatus')}>
                            <option value="">All</option>
                            <option value="A">Active</option>
                            <option value="N">Inactive</option>
                        </select>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AllUsersFilters;
